<script lang="ts" context="module">
    import { match } from 'ts-pattern';
    import { quadOut, quartInOut } from 'svelte/easing';
    import { fly } from 'svelte/transition';

    export function transitionIn(node: Element, params: { style: 'modal' | 'push' }) {
        return fly(node, {
            delay: 0,
            duration: 300,
            easing: quadOut,
            opacity: 0,
            y: 0,
            x: 0,
            ...match(params.style)
                .with('modal', () => ({
                    y: -50,
                }))
                .with('push', () => ({
                    x: 50,
                }))
                .exhaustive(),
        });
    }

    export function transitionOut(node: Element, params: { style: 'modal' | 'push' }) {
        return fly(node, {
            delay: 0,
            duration: 300,
            easing: quartInOut,
            opacity: 0,
            y: 0,
            x: 0,
            ...match(params.style)
                .with('modal', () => ({
                    y: -50,
                }))
                .with('push', () => ({
                    x: 50,
                }))
                .exhaustive(),
        });
    }
</script>

<script lang="ts">
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';
    import { createDialog } from '@melt-ui/svelte';
    import { melt } from '@melt-ui/svelte';

    export let dialog: ReturnType<typeof createDialog>;
    export let title: string;

    export let transitionStyle: 'modal' | 'push' = 'modal';

    export let closeIcon: 'cross' | 'chevron' = 'cross';

    const {
        elements: { portalled, overlay, content, title: titleElement, close },
        states: { open },
    } = dialog;
</script>

<div use:melt={$portalled}>
    {#if $open}
        <StackZIndex zIndex={'inc'} let:zIndex>
            <div
                use:melt={$overlay}
                class="fixed inset-0 z-[--z] bg-white/20"
                style={`--z: ${zIndex}`}
            />

            <StackZIndex zIndex={'inc'} let:zIndex>
                <div
                    use:melt={$content}
                    in:transitionIn={{ style: transitionStyle }}
                    out:transitionOut={{ style: transitionStyle }}
                    class="absolute top-0 left-0 right-0 h-dvh z-[--z] flex flex-col shadow-leo"
                    style={`--z: ${zIndex}`}
                >
                    <header
                        class="bg-white w-full rounded-t-[8px] mt-[10px] relative border-b border-0 border-solid border-gray-200"
                    >
                        <div class="h-[64px] flex items-center">
                            <button
                                type="button"
                                class="btn-reset absolute top-0 hover:opacity-50 w-[44px] h-full text-gray-800 flex items-center justify-center focus:outline-none focus:ring-1 ring-brand-blue focus:rounded-[4px]"
                                use:melt={$close}
                                aria-label="Close"
                            >
                                {#if closeIcon === 'cross'}
                                    <i
                                        class="tnp tnp-cross-small text-20"
                                        aria-hidden={true}
                                    ></i>
                                {:else if closeIcon === 'chevron'}
                                    <i
                                        class="tnp tnp-chevron-left text-20"
                                        aria-hidden={true}
                                    ></i>
                                {/if}
                            </button>
                            <div
                                class="text-13 font-bold text-gray-800 m-0 px-[20px] text-center w-full"
                                use:melt={$titleElement}
                            >
                                {title}
                            </div>
                        </div>
                    </header>
                    <div class="m-0 bg-gray-100 flex flex-col h-[calc(100dvh-75px)]">
                        <slot />
                        {#if $$slots.footer != null}
                            <div
                                class="px-[20px] py-[20px] flex flex-col items-center justify-center gap-y-[10px]"
                            >
                                <slot name="footer" />
                            </div>
                        {/if}
                    </div>
                </div>
            </StackZIndex>
        </StackZIndex>
    {/if}
</div>
